/* eslint-disable class-methods-use-this */
import * as THREE from "three";

// See all the params you can add here: https://docs.imgix.com/apis/url
const ULTRA_HIGH_QUALITY_CUBE_PARAMS = {
  auto: "format",
  q: 80,
  "max-w": 2048,
  "max-h": 2048,
  fit: "clip",
};

const HIGH_QUALITY_CUBE_PARAMS = {
  auto: "format",
  q: 80,
  w: 1024,
  h: 1024,
  fit: "clip",
};

const MEDIUM_QUALITY_CUBE_PARAMS = {
  auto: "format",
  q: 80,
  w: 800,
  h: 800,
};

const LOW_QUALITY_CUBE_PARAMS = {
  auto: "format",
  blur: 5,
  q: 50,
  w: 450,
  h: 450,
};

const CUBE_IMAGE_QUALITY_DOWNLOAD_ORDER = [
  LOW_QUALITY_CUBE_PARAMS,
  MEDIUM_QUALITY_CUBE_PARAMS,
  HIGH_QUALITY_CUBE_PARAMS,
  ULTRA_HIGH_QUALITY_CUBE_PARAMS,
];

// See all the params you can add here: https://docs.imgix.com/apis/url
// const ULTRA_HIGH_QUALITY_PARAMS = {
//   auto: "format",
//   q: 80,
//   w: 8192,
//   h: 4096,
// };

const HIGH_QUALITY_PARAMS = {
  auto: "format",
  q: 80,
  w: 4096,
  h: 2048,
};

const MEDIUM_QUALITY_PARAMS = {
  auto: "format",
  q: 80,
  w: 2048,
  h: 1024,
};

const LOW_QUALITY_PARAMS = {
  auto: "format",
  q: 50,
  blur: 30,
  w: 1024,
  h: 512,
};

export const THUMBNAIL_QUALITY_PARAMS = MEDIUM_QUALITY_PARAMS;

const IMAGE_QUALITY_DOWNLOAD_ORDER = [
  LOW_QUALITY_PARAMS,
  MEDIUM_QUALITY_PARAMS,
  HIGH_QUALITY_PARAMS,
  // ULTRA_HIGH_QUALITY_PARAMS
];

// const STEREO_ULTRA_HIGH_QUALITY_PARAMS = {
//   auto: "format",
//   q: 80,
//   w: 8192,
//   h: 4096,
//   fit: "crop",
// };

const STEREO_HIGH_QUALITY_PARAMS = {
  auto: "format",
  q: 80,
  w: 4096,
  h: 2048,
  fit: "crop",
};

const STEREO_MEDIUM_QUALITY_PARAMS = {
  auto: "format",
  q: 80,
  w: 2048,
  h: 1024,
  fit: "crop",
};

const STEREO_LOW_QUALITY_PARAMS = {
  auto: "format",
  q: 50,
  blur: 30,
  w: 1024,
  h: 512,
  fit: "crop",
};

const STEREO_IMAGE_QUALITY_DOWNLOAD_ORDER = [
  STEREO_LOW_QUALITY_PARAMS,
  STEREO_MEDIUM_QUALITY_PARAMS,
  STEREO_HIGH_QUALITY_PARAMS,
  // STEREO_ULTRA_HIGH_QUALITY_PARAMS,
];

export function limitWidth(url) {
    if (url !== undefined && (url.includes("imgix") || url.includes("imgo"))) {
      url = getUrlWithImgixParams(url, {
        "max-w": 6000
      });
    }

    return url;
}

function isHighDensity() {
  return (
    (window.matchMedia &&
      (window.matchMedia(
        "only screen and (min-resolution: 124dpi), only screen and (min-resolution: 1.3dppx), only screen and (min-resolution: 48.8dpcm)",
      ).matches ||
        window.matchMedia(
          "only screen and (-webkit-min-device-pixel-ratio: 1.3), only screen and (-o-min-device-pixel-ratio: 2.6/2), only screen and (min--moz-device-pixel-ratio: 1.3), only screen and (min-device-pixel-ratio: 1.3)",
        ).matches)) ||
    (window.devicePixelRatio && window.devicePixelRatio > 1.3)
  );
}

function getScreenResolution() {
  return {
    x: window.screen.width * window.devicePixelRatio,
    y: window.screen.height * window.devicePixelRatio,
  };
}

/**
 * Creates a URL object with the required imgix params attached
 * @param {String} url
 * @param {Object} imgixParams
 */
export function getUrlWithImgixParams(url, imgixParams, flip) {
  const urlObject = new URL(url);
  const paramsArray = Object.entries(imgixParams);
  for (const [key, value] of paramsArray) {
    urlObject.searchParams.set(String(key), String(value));
  }
  // if (flip) {
  //   urlObject.searchParams.set("flip", "v");
  // }
  return urlObject;
}

export function getStereoUrlWithImgixParams(url, imgixParams, half, flip) {
  const urlObject = getUrlWithImgixParams(url, imgixParams, flip);

  let widthIsInParams = false;
  for (const param of ["width", "w"]) {
    if (param in imgixParams) {
      widthIsInParams = true;
      urlObject.searchParams.set("h", String(imgixParams[param] / 2));
    }
  }

  if (!widthIsInParams) {
    urlObject.searchParams.set("h", "0.5");
    urlObject.searchParams.set("w", "1");
  }

  if (half === "top") {
    urlObject.searchParams.set("crop", "top");
  } else if (half === "bottom") {
    urlObject.searchParams.set("crop", "bottom");
  } else {
    console.warn("No half found");
    return false;
  }

  return urlObject;
}

/**
 *
 * @param stereo
 * @param tryHD
 * @param cube
 * @param curWidth
 * @returns {null}
 */
export function getHighestAvailableRes(
  stereo = false,
  tryHD = false,
  cube = false,
  curWidth = null,
) {
  let curW = curWidth === null ? getScreenResolution().x : curWidth;
  let maxWidth = curW * (tryHD || isHighDensity() ? 4 : 3);
  let highestAvailableRes = null;
  let qArray;

  if (cube) {
    qArray = CUBE_IMAGE_QUALITY_DOWNLOAD_ORDER;
  } else {
    qArray = !stereo
      ? IMAGE_QUALITY_DOWNLOAD_ORDER
      : STEREO_IMAGE_QUALITY_DOWNLOAD_ORDER;
  }
  for (let iq in qArray) {
    let qInfo = qArray[iq];
    if (
      qInfo.w <= maxWidth * 2 &&
      (!highestAvailableRes || qInfo.w > highestAvailableRes.w)
    ) {
      highestAvailableRes = qInfo;
    }
  }
  return highestAvailableRes;
}

export default class TextureManager {
  type;
  onPreloadStarted;
  onPreloadFinished;
  onCountUpdate;
  preloadedImages;

  constructor(
    onPreloadStarted = () => {},
    onPreloadFinished = () => {},
    onCountUpdate = () => {},
  ) {
    this.type = "global";
    this.onPreloadStarted = onPreloadStarted;
    this.onPreloadFinished = onPreloadFinished;
    this.onCountUpdate = onCountUpdate;
    this.preloadedImages = [];
    this.bitmapLoaderSupport = undefined;
    try {
      // ImageBitmap feature detection
      if ("createImageBitmap" in window) {
        const img = document.createElement("img");

        this.loaderDecided = window
          .createImageBitmap(img, { imageOrientation: "flipY" })
          .then(() => {
            this.loader = new THREE.ImageBitmapLoader();
            this.loader.setOptions({ imageOrientation: "flipY" });
            this.bitmapLoaderSupport = true;
            // console.log('Image bitmap supported');
          })
          .catch(err => {
            // If its a type error, options aren't supported
            if (err instanceof TypeError) {
              this.loader = new THREE.ImageLoader();
              this.bitmapLoaderSupport = false;

              // console.log('Image bitmap options not supported');
            } else {
              this.loader = new THREE.ImageBitmapLoader();
              this.bitmapLoaderSupport = true;
              this.loader.setOptions({ imageOrientation: "flipY" });

              // console.log('Image bitmap supported');
            }
          });
      } else {
        // console.log("Image bitmap not supported");
        throw new Error("Image bitmap not a feature");
      }
    } catch (err) {
      this.loaderDecided = true;
      // console.log("Image bitmap not supported");
      // console.warn("Global Texture Manager:", err);
      this.bitmapLoaderSupport = false;
      this.loader = new THREE.ImageLoader();
    }

    this.concurrent_count = 0;
    this.toLoad = 0;
    this.preloading = false;
    this.totalCount = 0;
    this.queue = [];
    this.loadingCritical = false;
  }

  /**
   *
   * @param image
   * @param flip
   * @param callback
   * @param stereo
   * @param isThumbnail
   * @param highestAvailableRes
   * @returns {boolean}
   */
  checkCached(
    image,
    flip,
    callback,
    stereo,
    isThumbnail = false,
    highestAvailableRes = null,
  ) {
    if (image.cube ?? false) {
      if (
        this.preloadedImages.includes(image.urls.full + "[CUBE]") &&
        !isThumbnail
      ) {
        this.loadCubeFromCache(image, flip, stereo, callback);
        return true;
      }
    } else {
      if (this.preloadedImages.includes(image.urls.full) && !isThumbnail) {
        let url;
        if (stereo) {
          url = getStereoUrlWithImgixParams(
            image.urls.full,
            highestAvailableRes,
            stereo.half,
            this.bitmapLoaderSupport,
          );
        } else {
          url = getUrlWithImgixParams(
            image.urls.full,
            highestAvailableRes,
            this.bitmapLoaderSupport,
          );
        }
        this.loadFromCache(url.href, flip, callback);
        return true;
      }
    }

    return false;
  }

  /**
   * Runs the callback an image is loaded.
   * This image may be the high or lower quality image.
   *
   * @param {Object} image
   * @param {boolean} flip - whether to flip the image
   * @param {function} callback - calls containing the loaded {THREE.Texture}
   * @param nonCritical
   * @param stereo
   * @param lowRes
   * @param resolution
   * @param isThumbnail
   * @param cube
   */
  async fetch({
    image,
    flip,
    callback,
    nonCritical = false,
    stereo = false,
    lowRes = false,
    resolution = false,
    isThumbnail = false,
  }) {
    // If not important wait until more important images downloaded first
    if (nonCritical && this.loadingCritical) {
      setTimeout(
        () =>
          this.fetch({
            image,
            flip,
            callback,
            nonCritical,
            stereo,
            lowRes,
            resolution,
            isThumbnail,
          }),
        500,
      );
      return;
    }

    if (!nonCritical) {
      this.loadingCritical = true;
    }

    let isCube = image.cube !== undefined;
    let highestAvailableRes = getHighestAvailableRes(stereo, false, isCube);
    let hasUrls = image.urls !== undefined;

    if (hasUrls) {
      // CHECK CACHED
      // Check if we already have this texture
      if (
        await this.checkCached(
          image,
          flip,
          callback,
          stereo,
          isThumbnail,
          highestAvailableRes,
        )
      ) {
        return;
      }

      // Add compression
      if (isCube) {
        return this.loadCubeFaces(
          image,
          flip,
          stereo,
          callback,
          false,
          highestAvailableRes,
          lowRes,
        );
      } else {
        // Check if already have this texture
        return this.fetchSingle({
          image,
          flip,
          callback,
          stereo,
          lowRes,
          resolution,
          isThumbnail,
          highestAvailableRes,
        });
      }
    } else if (image.url !== undefined) {
      // Backup - just get the image no special params
      return this.getImage(image.url, flip, image.url)
        .then(texture => {
          callback({ texture, full: true, res: 1 });
        })
        .catch(err => {
          console.log(err);
          callback({
            texture: null,
            full: false,
            res: 0,
          });
        });
    }

    console.warn("invalid image");
    console.warn(image);
  }

  /**
   *
   * @param image
   * @param flip
   * @param callback
   * @param stereo
   * @param lowRes
   * @param resolution
   * @param isThumbnail
   * @param highestAvailableRes
   * @returns {Promise<void>}
   */
  async fetchSingle({
    image,
    flip,
    callback,
    stereo,
    lowRes = false,
    resolution = false,
    isThumbnail = false,
    highestAvailableRes = null,
  }) {
    if (!highestAvailableRes) {
      highestAvailableRes = getHighestAvailableRes(stereo, false, false);
    }

    if (
      (this.preloadedImages[image.urls.full] ?? false) === true &&
      !isThumbnail
    ) {
      let url;

      if (stereo) {
        url = getStereoUrlWithImgixParams(
          image.urls.full,
          highestAvailableRes,
          stereo.half,
          this.bitmapLoaderSupport,
        );
      } else {
        url = getUrlWithImgixParams(
          image.urls.full,
          highestAvailableRes,
          this.bitmapLoaderSupport,
        );
      }
      this.loadFromCache(url.href, flip, callback);
      return;
    }

    // Create low quality images and download first
    if ("urls" in image && "full" in image.urls) {
      let varyingQualityUrls;
      if (image.urls.full !== undefined && (image.urls.full.includes("imgix") || image.urls.full.includes("imgo"))) {
        if (isThumbnail) {
          varyingQualityUrls = [
            getUrlWithImgixParams(
              image.urls.full,
              THUMBNAIL_QUALITY_PARAMS,
              this.bitmapLoaderSupport,
            ),
          ];
        } else {
          let imgOrder = [
            (!stereo
              ? IMAGE_QUALITY_DOWNLOAD_ORDER
              : STEREO_IMAGE_QUALITY_DOWNLOAD_ORDER)[0],
            highestAvailableRes,
          ];

          if (!stereo) {
            varyingQualityUrls = imgOrder.map(imgixParams =>
              getUrlWithImgixParams(
                image.urls.full,
                imgixParams,
                this.bitmapLoaderSupport,
              ),
            );
          } else {
            varyingQualityUrls = imgOrder.map(imgixParams =>
              getStereoUrlWithImgixParams(
                image.urls.full,
                imgixParams,
                stereo.half,
                this.bitmapLoaderSupport,
              ),
            );
          }
        }
      } else {
        varyingQualityUrls = [{ href: image.urls.full }];
      }

      try {
        const getImg = (url, res, full = false) => {
          return new Promise((resolve, reject) => {
            this.getImage(url.href, flip, image.urls.full)
              .then(texture => {
                resolve({
                  texture,
                  full: full,
                  res,
                });
              })
              .catch(err => {
                console.error(err);
                reject();
              });
          });
        };

        if (lowRes) {
          if (!(this.preloadedImages[image.urls.full] ?? false)) {
            const imgProm = getImg(varyingQualityUrls[0], 0, false);
            await imgProm.then(params => callback(params));
            this.loadingCritical = false;
            return;
          } else {
            resolution = "high";
          }
        }

        if (resolution === "high") {
          const imgProm = getImg(
            varyingQualityUrls[varyingQualityUrls.length - 1],
            0,
            true,
          );
          await imgProm.then(params => callback(params));
          this.loadingCritical = false;
          return;
        }

        const promises = varyingQualityUrls.map((url, index) =>
          getImg(
            url,
            (index + 1) / varyingQualityUrls.length,
            index === varyingQualityUrls.length - 1,
          ),
        );

        let loadHD = false;
        // render first resolved promise
        await Promise.any(promises).then(params => {
          if (!params.full) {
            loadHD = true;
          }
          callback(params);
        });

        // if first promise isn't the highest available resolution, wait for the higher one
        if (loadHD && varyingQualityUrls.length > 1) {
          await Promise.all(promises).then(params => {
            for (let p in params) {
              if (params[p].full) {
                callback(params[p]);
                return;
              }
            }
          });
        }

        this.loadingCritical = false;
      } catch (err) {
        console.error(err);
      }
    }
  }

  async loadCubeFaces(
    image,
    flip,
    stereo,
    callback = {},
    hasCached = false,
    highestAvailableRes = null,
    lowRes = true,
    stagger = true,
  ) {
    const getImg = (url, face = null, delay = 0) => {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          this.getImage(url, flip, url)
            .then(texture => {
              texture.name = face;
              resolve(texture);
            })
            .catch(err => {
              console.error(err);
              reject();
            });
        }, delay);
      });
    };

    if (!image.cube) {
      console.error("INVALID CUBE");
      console.error(image);
      return;
    }

    if (!highestAvailableRes) {
      highestAvailableRes = getHighestAvailableRes(stereo, false, true);
    }

    let loadHD = [];
    let hqProm = [];

    let hqUrl = [];
    let lqUrl = [];
    let face_order = ["front", "left", "right", "back", "below", "above"];

    let setTexture = callback.setTexture ?? null;
    let getPriorityFace = callback.getPriorityFace ?? null;
    let onLoaded;

    if (typeof callback === "function") {
      onLoaded = callback;
    } else {
      onLoaded = callback.onLoaded ?? null;
    }

    // let face_max_width = image.cube.width;
    let gtM = this;
    let vFaces = ["above", "below"];

    let queueFace = function (f) {
      if (!hasCached) {
        lqUrl[f] = getUrlWithImgixParams(
          image.cube.faces[f],
          LOW_QUALITY_CUBE_PARAMS,
          gtM.bitmapLoaderSupport,
        );
      }

      if (!lowRes || hasCached) {
        hqUrl[f] = getUrlWithImgixParams(
          image.cube.faces[f],
          highestAvailableRes,
          gtM.bitmapLoaderSupport,
        );
      }
    };

    let getLQ = function (face, delay = 0) {
      return getImg(lqUrl[face].href, face, delay);
    };

    let getHQ = function (face, delay = 0) {
      return getImg(hqUrl[face].href, face, delay);
    };

    for (let f in face_order) {
      let face = face_order[f];
      queueFace(face);
    }

    let lq_Prom = [];

    if (!hasCached) {
      // load lq
      for (let face in lqUrl) {
        lq_Prom.push(getLQ(face, 0));
      }

      // load all lq textures together
      let _lq = Promise.all(lq_Prom).then(textures => {
        for (let x in textures) {
          if (!loadHD.includes(textures[x].name)) {
            if (setTexture) {
              setTexture(textures[x], textures[x].name);
            }
          }
        }

        if (onLoaded) {
          onLoaded({ texture: null, full: false, res: 0.5 });
        }
        return textures;
      });

      if (lowRes) {
        return await _lq;
      }

      await _lq;
    }

    // let priorityFace = cube ? await cube.getScreenFace() : 'front'; // get forward face
    let priorityFace = getPriorityFace ? getPriorityFace() : "front"; // get forward face
    let hProm = []; // horizontal faces

    // pick faces either side to prioritize
    let nearFace = function (f) {
      if ("front" === f || "back" === f) {
        return ["left", "right"];
      } else if ("left" === f || "right" === f) {
        return ["front", "back"];
      }

      return [];
    };

    let nf;
    if (priorityFace) {
      nf = nearFace(priorityFace);
    }

    let hq_stagger_delay = stagger ? 250 : 0;

    // load hq
    for (let face in hqUrl) {
      let prom;
      let nfProm = false;
      if (priorityFace) {
        if (nf.includes(face)) {
          nfProm = true;
        }
        let _highP = face === priorityFace || nfProm;

        prom = getHQ(face, _highP ? 0 : hq_stagger_delay); // stagger load

        if (_highP) {
          hProm.push(prom);
        }
      } else {
        prom = getHQ(face, vFaces.includes(face) ? hq_stagger_delay : 0); // stagger load
      }

      hqProm.push(prom);
    }

    if (!hasCached) {
      // load hq horizontal faces
      Promise.all(hProm).then(textures => {
        for (let x in textures) {
          if (!loadHD.includes(textures[x].name)) {
            if (setTexture) {
              loadHD.push(textures[x].name);
              setTexture(textures[x], textures[x].name);
            }
          }
        }
        return textures;
      });
    }

    // load remaining hq textures (above/below)
    return await Promise.all(hqProm).then(textures => {
      for (let x in textures) {
        if (!loadHD.includes(textures[x].name)) {
          if (setTexture) {
            loadHD.push(textures[x].name);
            setTexture(textures[x], textures[x].name);
          }
        }
      }

      if (onLoaded) {
        onLoaded({ texture: null, full: true, res: 1 });
      }
      gtM.preloadedImages.push(image.urls.full + "[CUBE]");

      return textures;
    });
  }

  /**
   *
   * @param imageUrl
   * @param flip
   * @param callback
   * @returns {Promise<void>}
   */
  loadFromCache(imageUrl, flip, callback) {
    return this.getImage(imageUrl, flip, false).then(texture => {
      if (callback) {
        callback({ texture: texture, full: true, res: 1 });
      }
    });
  }

  /**
   *
   * @param image
   * @param flip
   * @param stereo
   * @param callback
   * @returns {Promise<unknown[]>}
   */
  loadCubeFromCache(image, flip, stereo, callback) {
    return this.loadCubeFaces(image, flip, stereo, callback, true, null, false);
  }

  /**
   * Fetches the image using THREE.TextureLoader
   *
   * @param {string} imageUrl
   * @param {boolean} flip - whether to flip the image.
   * @param {string} cacheIdent
   */
  getImage(imageUrl, flip, cacheIdent) {
    return new Promise((resolve, reject) => {
      let url = imageUrl;
      url = url.replace("dev.movein360.com", "movein.theatro360.com");

      const load = () => {
        this.loader.load(
          url,
          imageBitmap => {
            const texture = new THREE.CanvasTexture(imageBitmap);
            if (flip === true) {
              texture.wrapS = THREE.RepeatWrapping;
              texture.repeat.x = -1;
            }
            // Save to cache
            if (cacheIdent) {
              this.preloadedImages[cacheIdent] = true;
            }
            resolve(texture);
          },
          undefined,
          err => {
            reject(err);
          },
        );
      };

      if (this.loaderDecided === true || this.loaderDecided.isResolved) {
        load();
      } else {
        this.loaderDecided.then(() => {
          load();
        });
      }
    });
  }
}
